// PUBLIC business profile — /company/:slug (premium civil capability statement)
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Mail, Phone, MapPin, Globe, Share2, X, ChevronRight, Check, HardHat,
  Shovel, Droplets, Route as RouteIcon, Building2, Zap, LandPlot, Loader2,
  CalendarDays, Users, Trophy, Layers, Award,
} from "lucide-react";
import { MarketingHeader } from "@/components/marketing/MarketingHeader";
import { MarketingFooter } from "@/components/marketing/MarketingSections2";
import { SEO } from "@/lib/seo";

const API = process.env.REACT_APP_BACKEND_URL;
const img = (p) => `${API}/api/public/bp-image?path=${encodeURIComponent(p)}`;
const SVC_ICONS = { earthworks: Shovel, drainage: Droplets, roading: RouteIcon, siteworks: HardHat, utilities: Zap, subdivisions: LandPlot, retaining: Layers, concrete: Building2 };
const FALLBACK_HERO = "/marketing/civil.jpg";

const track = (slug, kind, project_id) => axios.post(`${API}/api/public/company/${slug}/event`, { kind, project_id }).catch(() => {});
const safeUrl = (u) => (u && /^https?:\/\//i.test(u) ? u : u ? `https://${u}` : null);

export function Header() {
  return <MarketingHeader />;
}

export default function PublicProfile() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [enquire, setEnquire] = useState(false);
  const [active, setActive] = useState("about");
  const sectionRefs = useRef({});

  useEffect(() => {
    axios.get(`${API}/api/public/company/${slug}`).then((r) => {
      setP(r.data);
      track(slug, "view");
    }).catch(() => setErr(true));
  }, [slug]);

  const vis = (k) => (p?.sections || []).find((s) => s.key === k)?.visible !== false;
  const specCats = useMemo(() => Object.entries(p?.specialist_services || {}).filter(([, v]) => (v || []).length > 0), [p]);
  const navItems = useMemo(() => {
    if (!p) return [];
    const items = [];
    if (p.about) items.push(["about", "Overview"]);
    if (vis("services") && (p.services || []).length) items.push(["services", "Services"]);
    if (vis("projects") && (p.projects || []).length) items.push(["projects", "Projects"]);
    if (specCats.length) items.push(["capability", "Capability"]);
    if (vis("plant") && p.plant_display?.enabled) items.push(["plant", "Plant"]);
    if (vis("accreditations") && (p.accreditations || []).length) items.push(["accreditations", "Accreditations"]);
    if (vis("gallery") && (p.gallery || []).length) items.push(["gallery", "Gallery"]);
    items.push(["contact", "Contact"]);
    return items;
    // eslint-disable-next-line
  }, [p, specCats]);

  useEffect(() => {
    const onScroll = () => {
      let cur = navItems[0]?.[0];
      for (const [key] of navItems) {
        const el = sectionRefs.current[key];
        if (el && el.getBoundingClientRect().top < 160) cur = key;
      }
      if (cur) setActive(cur);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [navItems]);

  if (err) return (<><Header /><div className="text-center py-24"><h1 className="font-display font-black text-3xl uppercase">Profile not found</h1><p className="text-[#52616c] mt-2 text-sm">This business profile isn't available.</p></div></>);
  if (!p) return (<><Header /><div className="flex justify-center py-24"><Loader2 className="w-7 h-7 animate-spin text-[#a63c0b]" /></div></>);

  const share = () => {
    const url = window.location.href;
    if (navigator.share) navigator.share({ title: p.name, url }).catch(() => {});
    else { navigator.clipboard.writeText(url); }
    track(slug, "share");
  };
  const goTo = (key) => {
    const el = sectionRefs.current[key];
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 120, behavior: "smooth" });
  };
  const setRef = (key) => (el) => { sectionRefs.current[key] = el; };
  const svcTitles = (p.services || []).map((s) => s.title);
  const website = safeUrl(p.contact?.website);
  const subsFor = (title) => {
    const t = (title || "").toLowerCase();
    const hit = Object.entries(p.specialist_services || {}).find(([k]) => k.toLowerCase() === t);
    return hit ? hit[1] : [];
  };
  const statDefs = [
    ["years", "Years experience", CalendarDays, "+"],
    ["projects_completed", "Projects completed", Trophy, "+"],
    ["team_size", "Team size", Users, ""],
    ["based", "Based in", MapPin, ""],
  ];
  const stats = statDefs.filter(([k]) => p.stats?.[k]);

  return (
    <div className="bg-[#f6f5f1] text-[#142635] min-h-screen pb-16 md:pb-0" data-testid="public-profile">
      <SEO
        title={`${p.name} | ${svcTitles.slice(0, 2).join(" & ") || "Civil Contractor"} ${p.contact?.location || "New Zealand"} | Fieldlink`}
        description={(p.about || `${p.name} — civil contractor${p.contact?.location ? ` in ${p.contact.location}` : ""}. View services, projects and request a quote.`).slice(0, 158)}
        path={`/company/${slug}`}
        ogImage={p.cover_path ? img(p.cover_path) : (p.logo_path ? img(p.logo_path) : undefined)}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: p.name,
          description: (p.about || "").slice(0, 300) || undefined,
          url: `https://www.fieldlink.co.nz/company/${slug}`,
          image: p.logo_path ? img(p.logo_path) : undefined,
          telephone: p.contact?.phone || undefined,
          address: p.contact?.location ? { "@type": "PostalAddress", addressLocality: p.contact.location, addressCountry: "NZ" } : undefined,
          areaServed: (p.service_areas || []).map((a) => ({ "@type": "Place", name: a })),
          makesOffer: svcTitles.map((s) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name: s } })),
          foundingDate: p.established_year || undefined,
        }}
      />
      <Header />

      {/* HERO */}
      <div className="relative h-[300px] md:h-[400px] bg-[#e9eeec]">
        <img src={p.cover_path ? img(p.cover_path) : FALLBACK_HERO} alt={`${p.name} cover`} className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-white/85 via-white/30 to-white/20" />
        <div className="absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r from-[#FF5F15] via-[#FF5F15]/40 to-transparent" />
        <div className="absolute bottom-14 left-0 right-0 max-w-[1280px] mx-auto px-4 md:px-8">
          {p.tagline && <h1 className="font-display font-black uppercase text-[#142635] text-3xl md:text-5xl leading-[0.95] max-w-[680px] drop-shadow">{p.tagline}</h1>}
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            {svcTitles.slice(0, 4).map((s) => <span key={s} className="text-[10px] font-black uppercase tracking-wider bg-white backdrop-blur text-[#142635] border border-[#cbd3d5] rounded px-2 py-1">{s}</span>)}
            {p.contact?.location && <span className="text-[11px] font-bold text-[#344451] flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-[#a63c0b]" /> {p.contact.location}</span>}
          </div>
        </div>
      </div>

      {/* IDENTITY CARD */}
      <div className="max-w-[1280px] mx-auto px-4 md:px-8">
        <div className="relative -mt-14 bg-white rounded-xl shadow-[0_30px_80px_rgba(0,0,0,0.6)] border border-[#cbd3d5] p-5 md:p-7 flex flex-col md:flex-row gap-6 items-start">
          {p.logo_path && (
            <div className="w-24 h-24 md:w-28 md:h-28 rounded-xl border border-[#cbd3d5] bg-white p-2 shrink-0 flex items-center justify-center">
              <img src={img(p.logo_path)} alt={`${p.name} logo`} className="max-w-full max-h-full object-contain" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h2 className="font-display font-black uppercase text-2xl md:text-3xl tracking-tight" data-testid="pp-name">{p.name}</h2>
            {svcTitles.length > 0 && <div className="text-[12px] font-bold text-[#a63c0b] uppercase tracking-wide mt-1">{svcTitles.slice(0, 5).join(" • ")}</div>}
            {p.contact?.location && <div className="text-[12px] text-[#52616c] flex items-center gap-1 mt-1"><MapPin className="w-3.5 h-3.5 text-[#a63c0b]" /> {p.contact.location}</div>}
            {p.about && <p className="text-sm text-[#52616c] mt-2.5 max-w-[680px] line-clamp-2">{p.about}</p>}
          </div>
          <div className="flex gap-2 flex-wrap shrink-0 md:flex-col lg:flex-row">
            <button onClick={() => { setEnquire(true); track(slug, "quote_click"); }} className="bg-[#FF5F15] hover:bg-[#E65100] text-[#142635] text-[11px] font-black uppercase tracking-wider px-5 py-2.5 rounded-md" data-testid="pp-request-quote">Request a quote</button>
            {p.contact?.email && <a href={`mailto:${p.contact.email}`} onClick={() => track(slug, "contact_click")} className="border border-[#cbd3d5] bg-white hover:border-[#FF5F15] text-[#142635] text-[11px] font-black uppercase tracking-wider px-5 py-2.5 rounded-md text-center" data-testid="pp-contact">Contact</a>}
            {website && <a href={website} target="_blank" rel="noopener noreferrer" onClick={() => track(slug, "website_click")} className="border border-[#cbd3d5] bg-white hover:border-[#FF5F15] text-[#142635] text-[11px] font-black uppercase tracking-wider px-5 py-2.5 rounded-md inline-flex items-center justify-center gap-1.5" data-testid="pp-website"><Globe className="w-3.5 h-3.5" /> Website</a>}
            <button onClick={share} className="border border-[#cbd3d5] bg-white hover:border-[#FF5F15] text-[#142635] text-[11px] font-black uppercase tracking-wider px-4 py-2.5 rounded-md inline-flex items-center justify-center gap-1.5" data-testid="pp-share"><Share2 className="w-3.5 h-3.5" /> Share</button>
          </div>
        </div>

        {/* STATS STRIP */}
        {stats.length > 0 && (
          <div className={`grid grid-cols-2 md:grid-cols-${Math.min(stats.length, 4)} gap-3 mt-5`} data-testid="pp-stats">
            {stats.map(([k, l, Icon, suf]) => (
              <div key={k} className="flex items-center gap-3 border border-[#cbd3d5] bg-white rounded-xl px-4 py-3.5">
                <Icon className="w-5 h-5 text-[#a63c0b] shrink-0" strokeWidth={1.8} />
                <div>
                  <div className="font-display font-black text-xl leading-none">{p.stats[k]}{/^\d+$/.test(String(p.stats[k])) ? suf : ""}</div>
                  <div className="text-[9.5px] font-bold uppercase tracking-widest text-[#52616c] mt-0.5">{l}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* STICKY PROFILE NAV */}
      <div className="sticky top-0 z-30 bg-[rgba(7,9,13,0.94)] backdrop-blur-xl border-b border-[#cbd3d5] mt-6" data-testid="pp-nav">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 flex gap-1 overflow-x-auto no-scrollbar">
          {navItems.map(([key, label]) => (
            <button key={key} onClick={() => goTo(key)} data-testid={`pp-nav-${key}`}
              className={`shrink-0 px-4 py-3 text-[11px] font-black uppercase tracking-wider border-b-2 transition-colors ${active === key ? "border-[#FF5F15] text-[#a63c0b]" : "border-transparent text-[#52616c] hover:text-[#142635]"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-4 md:px-8">
        {/* ABOUT + AT A GLANCE */}
        {p.about && (
          <section ref={setRef("about")} className="mt-10 grid lg:grid-cols-[1fr_320px] gap-8">
            <div>
              <h3 className="font-display font-black uppercase text-2xl tracking-tight">About us</h3>
              <p className="text-[15px] leading-relaxed text-[#465562] mt-3 whitespace-pre-wrap">{p.about}</p>
              {p.mission && <p className="text-[15px] leading-relaxed text-[#465562] mt-4 whitespace-pre-wrap">{p.mission}</p>}
              {(p.why_us || []).length > 0 && (
                <div className="mt-6" data-testid="pp-why-us">
                  <div className="text-[11px] font-black uppercase tracking-widest text-[#a63c0b]">Why choose us</div>
                  <ul className="mt-2.5 grid sm:grid-cols-2 gap-x-6 gap-y-2">
                    {p.why_us.map((w) => <li key={w} className="text-[13.5px] font-semibold text-[#465562] flex items-start gap-2"><Check className="w-4 h-4 text-[#a63c0b] shrink-0 mt-0.5" /> {w}</li>)}
                  </ul>
                </div>
              )}
            </div>
            <aside className="bg-[#e9eeec] text-[#142635] rounded-xl p-5 h-fit">
              <div className="text-[10px] font-black uppercase tracking-widest text-[#a63c0b]">At a glance</div>
              <dl className="mt-3 space-y-3">
                {p.established_year && <div className="flex justify-between border-b border-[#cbd3d5] pb-2"><dt className="text-[12px] text-[#52616c] font-bold">Established</dt><dd className="text-[13px] font-black">{p.established_year}</dd></div>}
                {p.business_type && <div className="flex justify-between border-b border-[#cbd3d5] pb-2"><dt className="text-[12px] text-[#52616c] font-bold">Business type</dt><dd className="text-[13px] font-black">{p.business_type}</dd></div>}
                {p.stats?.years && <div className="flex justify-between border-b border-[#cbd3d5] pb-2"><dt className="text-[12px] text-[#52616c] font-bold">Experience</dt><dd className="text-[13px] font-black">{p.stats.years}+ years</dd></div>}
                {p.stats?.team_size && <div className="flex justify-between border-b border-[#cbd3d5] pb-2"><dt className="text-[12px] text-[#52616c] font-bold">Team</dt><dd className="text-[13px] font-black">{p.stats.team_size}</dd></div>}
                {(p.stats?.based || p.contact?.location) && <div className="flex justify-between border-b border-[#cbd3d5] pb-2"><dt className="text-[12px] text-[#52616c] font-bold">Based</dt><dd className="text-[13px] font-black">{p.stats?.based || p.contact?.location}</dd></div>}
                {(p.service_areas || []).length > 0 && <div className="flex justify-between border-b border-[#cbd3d5] pb-2 gap-4"><dt className="text-[12px] text-[#52616c] font-bold shrink-0">Service area</dt><dd className="text-[13px] font-black text-right">{p.service_areas.slice(0, 3).join(", ")}</dd></div>}
                {svcTitles.length > 0 && <div className="flex justify-between"><dt className="text-[12px] text-[#52616c] font-bold">Core services</dt><dd className="text-[13px] font-black">{svcTitles.length}</dd></div>}
              </dl>
            </aside>
          </section>
        )}

        {/* SERVICES */}
        {vis("services") && svcTitles.length > 0 && (
          <section ref={setRef("services")} className="mt-12">
            <h3 className="font-display font-black uppercase text-2xl tracking-tight">Our services</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-5">
              {(p.services || []).map((s) => {
                const Icon = SVC_ICONS[(s.title || "").toLowerCase()] || Building2;
                const subs = subsFor(s.title);
                return (
                  <div key={s.title} className="border border-[#cbd3d5] bg-white rounded-xl p-5 hover:border-[#FF5F15]/60 transition-colors" data-testid={`pp-service-${(s.title || "").toLowerCase()}`}>
                    <Icon className="w-7 h-7 text-[#a63c0b]" strokeWidth={1.7} />
                    <div className="font-display font-black uppercase text-[15px] mt-3">{s.title}</div>
                    {s.description && <div className="text-[12px] text-[#52616c] mt-1">{s.description}</div>}
                    {subs.length > 0 && (
                      <ul className="mt-3 space-y-1">
                        {subs.slice(0, 5).map((sub) => (
                          <li key={sub} className="text-[11.5px] text-[#52616c] flex items-center gap-1.5"><Check className="w-3 h-3 text-[#a63c0b] shrink-0" /> {sub}</li>
                        ))}
                        {subs.length > 5 && <li className="text-[10.5px] font-bold text-[#52616c]">+ {subs.length - 5} more</li>}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* PROJECTS */}
        {vis("projects") && (p.projects || []).length > 0 && (
          <section ref={setRef("projects")} className="mt-12">
            <h3 className="font-display font-black uppercase text-2xl tracking-tight">Featured projects</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-5">
              {p.projects.map((pr) => (
                <Link key={pr.id} to={`/company/${slug}/projects/${pr.slug}`} onClick={() => track(slug, "project_view", pr.id)}
                  className="group border border-[#cbd3d5] rounded-xl overflow-hidden bg-white hover:border-[#FF5F15]/50 transition-colors" data-testid={`pp-project-${pr.id}`}>
                  <div className="aspect-[16/10] overflow-hidden bg-white">
                    {pr.cover_path ? <img src={img(pr.cover_path)} alt={pr.name} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300" loading="lazy" />
                      : <div className="w-full h-full flex items-center justify-center"><HardHat className="w-9 h-9 text-[#465562]" /></div>}
                  </div>
                  <div className="p-4">
                    <div className="font-display font-black uppercase text-[15px] leading-tight">{pr.name}</div>
                    <div className="text-[11px] font-bold text-[#52616c] mt-1 flex items-center gap-1">{pr.location && <><MapPin className="w-3 h-3 text-[#a63c0b]" /> {pr.location}</>}{pr.completion_date && <span className="ml-1 text-[#52616c]">· {String(pr.completion_date).slice(0, 4)}</span>}</div>
                    {(pr.services || []).length > 0 && <div className="flex flex-wrap gap-1 mt-2">{pr.services.slice(0, 3).map((s) => <span key={s} className="text-[9px] font-black uppercase tracking-wider bg-[#FF5F15]/10 text-[#a63c0b] rounded px-1.5 py-0.5">{s}</span>)}</div>}
                    {pr.description && <p className="text-[12px] text-[#52616c] mt-2 line-clamp-2">{pr.description}</p>}
                    <span className="text-[10.5px] font-black uppercase tracking-wider inline-flex items-center gap-1 mt-2.5 text-[#465562] group-hover:text-[#a63c0b] transition-colors">View project <ChevronRight className="w-3 h-3" /></span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* CAPABILITY */}
        {specCats.length > 0 && (
          <section ref={setRef("capability")} className="mt-12" data-testid="pp-capability">
            <h3 className="font-display font-black uppercase text-2xl tracking-tight">Our capability</h3>
            <p className="text-sm text-[#52616c] mt-1">What we deliver, in detail.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-5">
              {specCats.map(([cat, subs]) => (
                <div key={cat} className="bg-white border border-[#cbd3d5] rounded-xl p-5">
                  <div className="font-display font-black uppercase text-[13px] text-[#a63c0b]">{cat}</div>
                  <ul className="mt-2.5 space-y-1.5">
                    {subs.map((s) => <li key={s} className="text-[12.5px] text-[#465562] flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#a63c0b] shrink-0" /> {s}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* PLANT */}
        {vis("plant") && p.plant_display?.enabled && (
          <section ref={setRef("plant")} className="mt-12 bg-[#e9eeec] text-[#142635] rounded-2xl p-6 md:p-8">
            <h3 className="font-display font-black uppercase text-2xl tracking-tight">Plant & equipment</h3>
            {p.plant_display.mode === "summary" ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5">
                {(p.plant_display.categories || []).map((c) => (
                  <div key={c.label} className="text-center border border-[#cbd3d5] rounded-xl py-5">
                    <div className="font-display font-black text-3xl text-[#a63c0b]">{c.count}</div>
                    <div className="text-[10.5px] font-bold uppercase tracking-widest text-[#52616c] mt-1">{c.label}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
                {(p.plant_display.featured || []).map((v, i) => (
                  <div key={v.id || i} className="border border-[#cbd3d5] rounded-xl overflow-hidden">
                    {v.photo_path && <img src={img(v.photo_path)} alt={v.name} className="h-28 w-full object-cover" loading="lazy" />}
                    <div className="p-2.5 font-display font-bold uppercase text-[12px]">{v.name}</div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* ACCREDITATIONS */}
        {vis("accreditations") && (p.accreditations || []).length > 0 && (
          <section ref={setRef("accreditations")} className="mt-12">
            <h3 className="font-display font-black uppercase text-2xl tracking-tight">Accreditations & memberships</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-4">
              {p.accreditations.map((a, i) => (
                <div key={i} className="flex items-center gap-3 border border-[#cbd3d5] bg-white rounded-xl px-4 py-3.5">
                  {a.logo_path ? <img src={img(a.logo_path)} alt="" className="h-9 w-9 object-contain shrink-0" /> : <Award className="w-5 h-5 text-[#a63c0b] shrink-0" />}
                  <span className="font-bold text-[13px] leading-tight">{a.name}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* SERVICE AREA */}
        {(p.service_areas || []).length > 0 && (
          <section className="mt-12" data-testid="pp-service-area">
            <h3 className="font-display font-black uppercase text-2xl tracking-tight">Where we work</h3>
            <div className="flex flex-wrap gap-2 mt-4">
              {p.service_areas.map((a) => (
                <span key={a} className="inline-flex items-center gap-1.5 border border-[#cbd3d5] bg-white rounded-full px-4 py-2 text-[12.5px] font-bold text-[#465562]"><MapPin className="w-3.5 h-3.5 text-[#a63c0b]" /> {a}</span>
              ))}
            </div>
          </section>
        )}

        {/* GALLERY */}
        {vis("gallery") && (p.gallery || []).length > 0 && (
          <section ref={setRef("gallery")} className="mt-12">
            <h3 className="font-display font-black uppercase text-2xl tracking-tight">Our work</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5 mt-5">
              {p.gallery.map((g, i) => (
                <button key={i} onClick={() => setLightbox(g.path)} className="rounded-lg overflow-hidden group" data-testid={`pp-gallery-${i}`}>
                  <img src={img(g.path)} alt={g.caption || `${p.name} project work`} className="h-44 md:h-52 w-full object-cover group-hover:scale-[1.04] transition-transform duration-300" loading="lazy" />
                </button>
              ))}
            </div>
          </section>
        )}

        {/* TEAM */}
        {vis("team") && (p.team || []).length > 0 && (
          <section className="mt-12">
            <h3 className="font-display font-black uppercase text-2xl tracking-tight">Our team</h3>
            <div className="flex flex-wrap gap-3 mt-4">
              {p.team.map((t, i) => (
                <div key={i} className="border border-[#cbd3d5] bg-white rounded-xl px-5 py-3 text-center">
                  <div className="font-display font-bold uppercase text-sm">{t.name}</div>
                  <div className="text-[10.5px] font-bold uppercase text-[#a63c0b]">{t.role}</div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* CONTACT CTA */}
      {vis("contact") && (
        <section ref={setRef("contact")} className="mt-14 bg-[#e9eeec] text-[#142635]">
          <div className="max-w-[900px] mx-auto px-4 py-14 text-center">
            <h3 className="font-display font-black uppercase text-3xl md:text-4xl tracking-tight">Have a project coming up?</h3>
            <p className="text-[#52616c] mt-2">Send your plans directly to {p.name}.</p>
            <div className="flex justify-center gap-3 mt-6 flex-wrap">
              <button onClick={() => { setEnquire(true); track(slug, "quote_click"); }} className="bg-[#FF5F15] hover:bg-[#E65100] text-[#142635] text-[12px] font-black uppercase tracking-wider px-8 py-3.5 rounded-md" data-testid="pp-cta-quote">Request a quote</button>
              {p.contact?.email && <a href={`mailto:${p.contact.email}`} onClick={() => track(slug, "contact_click")} className="border border-[#cbd3d5] hover:border-[#FF5F15] text-[12px] font-black uppercase tracking-wider px-8 py-3.5 rounded-md">Contact us</a>}
            </div>
            <div className="flex justify-center gap-5 mt-6 text-[12px] text-[#52616c] flex-wrap">
              {p.contact?.phone && <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-[#a63c0b]" /> {p.contact.phone}</span>}
              {p.contact?.email && <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-[#a63c0b]" /> {p.contact.email}</span>}
              {website && <a href={website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:text-[#142635]"><Globe className="w-3.5 h-3.5 text-[#a63c0b]" /> Website</a>}
            </div>
          </div>
          <div className="border-t border-[#cbd3d5] py-4 text-center text-[11px] text-[#52616c]">
            Powered by <a href="/" className="font-black text-[#465562] hover:text-[#a63c0b]">FIELDLINK</a> — civil construction software for serious contractors.
          </div>
        </section>
      )}
      <MarketingFooter />

      {/* sticky mobile CTA */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-[#cbd3d5] p-3">
        <button onClick={() => { setEnquire(true); track(slug, "quote_click"); }} className="w-full bg-[#FF5F15] text-[#142635] text-[12px] font-black uppercase tracking-wider py-3 rounded-md" data-testid="pp-mobile-quote">Request a quote</button>
      </div>
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="pp-lightbox">
          <img src={img(lightbox)} alt="" className="max-h-[90vh] max-w-full rounded-lg" />
          <button className="absolute top-4 right-4 text-white"><X className="w-7 h-7" /></button>
        </div>
      )}
      {enquire && <EnquiryForm slug={slug} services={svcTitles} onClose={() => setEnquire(false)} />}
    </div>
  );
}

export function EnquiryForm({ slug, services, onClose, source, searchService, searchLocation }) {
  const [f, setF] = useState({ name: "", company: "", email: "", phone: "", project_name: "", location: "", description: "", start_date: "" });
  const [svc, setSvc] = useState([]);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF((o) => ({ ...o, [k]: e.target.value }));
  const submit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      fd.append("services", svc.join(","));
      if (source) fd.append("source", source);
      if (searchService) fd.append("search_service", searchService);
      if (searchLocation) fd.append("search_location", searchLocation);
      files.slice(0, 5).forEach((file) => fd.append("files", file));
      await axios.post(`${API}/api/public/company/${slug}/enquire`, fd);
      setDone(true);
    } catch (e) { alert(e.response?.data?.detail || "Something went wrong — please try again"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto" data-testid="pp-enquiry-modal">
      <div className="bg-white text-[#142635] rounded-2xl border border-[#cbd3d5] max-w-lg w-full p-6 my-8 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#52616c] hover:text-[#142635]" data-testid="pp-enq-close"><X className="w-5 h-5" /></button>
        {done ? (
          <div className="text-center py-8">
            <Check className="w-10 h-10 mx-auto text-emerald-500" />
            <h3 className="font-display font-black uppercase text-xl mt-3">Enquiry sent</h3>
            <p className="text-sm text-[#52616c] mt-1">Your project details are on their way. Expect a reply soon.</p>
          </div>
        ) : (
          <>
            <h3 className="font-display font-black uppercase text-xl">Request a quote</h3>
            <div className="grid grid-cols-2 gap-2.5 mt-4 text-sm">
              {[["name", "Your name *"], ["company", "Company"], ["email", "Email *"], ["phone", "Phone"], ["project_name", "Project name"], ["location", "Location"]].map(([k, l]) => (
                <div key={k}><label className="text-[10px] font-bold uppercase tracking-widest text-[#52616c]">{l}</label>
                  <input value={f[k]} onChange={set(k)} className="w-full h-9 mt-0.5 border border-[#cbd3d5] bg-white text-[#142635] rounded-md px-2.5 text-sm outline-none focus:border-[#FF5F15]" data-testid={`enq-${k}`} /></div>
              ))}
            </div>
            {services.length > 0 && (
              <div className="mt-3"><label className="text-[10px] font-bold uppercase tracking-widest text-[#52616c]">Work required</label>
                <div className="flex flex-wrap gap-1.5 mt-1">{services.map((s) => (
                  <button key={s} onClick={() => setSvc((o) => o.includes(s) ? o.filter((x) => x !== s) : [...o, s])}
                    className={`h-7 px-2.5 rounded-md text-[10.5px] font-bold border ${svc.includes(s) ? "border-[#FF5F15] text-[#a63c0b] bg-[#FF5F15]/10" : "border-[#cbd3d5] text-[#52616c]"}`} data-testid={`enq-svc-${s}`}>{s}</button>))}</div></div>
            )}
            <div className="mt-3"><label className="text-[10px] font-bold uppercase tracking-widest text-[#52616c]">Project description *</label>
              <textarea value={f.description} onChange={set("description")} rows={3} className="w-full mt-0.5 border border-[#cbd3d5] bg-white text-[#142635] rounded-md px-2.5 py-2 text-sm outline-none focus:border-[#FF5F15]" data-testid="enq-description" /></div>
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              <label className="text-[11px] font-bold uppercase text-[#a63c0b] cursor-pointer">+ Upload plans ({files.length})
                <input type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.dwg,.zip,.doc,.docx" className="hidden" onChange={(e) => setFiles([...e.target.files])} data-testid="enq-files" /></label>
              <div><label className="text-[10px] font-bold uppercase tracking-widest text-[#52616c] mr-1.5">Start date</label>
                <input type="date" value={f.start_date} onChange={set("start_date")} className="h-8 border border-[#cbd3d5] bg-white text-[#142635] rounded-md px-2 text-xs" /></div>
            </div>
            <button onClick={submit} disabled={busy || !f.name || !f.email || f.description.length < 5}
              className="w-full mt-4 bg-[#FF5F15] hover:bg-[#E65100] disabled:opacity-50 text-[#142635] text-[12px] font-black uppercase tracking-wider py-3 rounded-md" data-testid="enq-send">
              {busy ? "Sending…" : "Send enquiry"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function PublicProject() {
  const { slug, pslug } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [enquire, setEnquire] = useState(false);
  useEffect(() => {
    axios.get(`${API}/api/public/company/${slug}/projects/${pslug}`).then((r) => {
      setD(r.data);
      document.title = `${r.data.project.name} — ${r.data.company.name} | Fieldlink`;
    }).catch(() => setErr(true));
  }, [slug, pslug]);
  if (err) return (<><Header /><div className="text-center py-24 font-display font-black text-2xl uppercase">Project not found</div></>);
  if (!d) return (<><Header /><div className="flex justify-center py-24"><Loader2 className="w-7 h-7 animate-spin text-[#a63c0b]" /></div></>);
  const pr = d.project;
  const pjDesc = (pr.description || `${pr.name} — delivered by ${d.company.name}${pr.location ? ` in ${pr.location}` : ""}.`).slice(0, 158);
  const details = [
    ["Project type", pr.category],
    ["Location", pr.location],
    ["Completed", pr.completion_date ? String(pr.completion_date).slice(0, 4) : null],
    ["Client", pr.client_name],
  ].filter(([, v]) => v);
  return (
    <div className="bg-[#f6f5f1] text-[#142635] min-h-screen" data-testid="public-project">
      <SEO
        title={`${pr.name} — ${d.company.name} | Fieldlink`}
        description={pjDesc}
        path={`/company/${slug}/projects/${pslug}`}
        ogImage={pr.cover_path ? img(pr.cover_path) : undefined}
      />
      <Header />
      <div className="relative h-[300px] md:h-[420px] bg-[#e9eeec]">
        {pr.cover_path ? <img src={img(pr.cover_path)} alt={pr.name} className="absolute inset-0 w-full h-full object-cover opacity-70" />
          : <img src={FALLBACK_HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />}
        <div className="absolute inset-0 bg-gradient-to-t from-white/85 via-white/30 to-white/20" />
        <div className="absolute bottom-8 left-0 right-0 max-w-[1100px] mx-auto px-4 md:px-8">
          <Link to={`/company/${slug}`} className="text-[11px] font-bold uppercase tracking-widest text-[#465562] hover:text-[#142635] inline-flex items-center gap-1.5" data-testid="pj-back">
            {d.company.logo_path && <img src={img(d.company.logo_path)} alt="" className="w-6 h-6 object-contain bg-white rounded p-0.5" />}
            ← {d.company.name}
          </Link>
          <h1 className="font-display font-black uppercase text-[#142635] text-3xl md:text-5xl mt-2 leading-[0.95]">{pr.name}</h1>
          <div className="text-[12px] font-bold text-[#a63c0b] uppercase mt-2">{[pr.category, pr.location].filter(Boolean).join(" • ")}</div>
        </div>
      </div>
      <div className="max-w-[1100px] mx-auto px-4 md:px-8 py-10">
        <div className="grid lg:grid-cols-[1fr_300px] gap-8">
          <div>
            <h2 className="font-display font-black uppercase text-xl tracking-tight">About the project</h2>
            {pr.description ? <p className="text-[15px] leading-relaxed text-[#465562] mt-3 whitespace-pre-wrap">{pr.description}</p>
              : <p className="text-sm text-[#52616c] mt-3">Delivered by {d.company.name}.</p>}
          </div>
          <aside className="space-y-4">
            {details.length > 0 && (
              <div className="border border-[#cbd3d5] bg-white rounded-xl p-5">
                <div className="text-[10px] font-black uppercase tracking-widest text-[#52616c]">Project details</div>
                <dl className="mt-2.5 space-y-2">
                  {details.map(([k, v]) => <div key={k} className="flex justify-between gap-3"><dt className="text-[12px] text-[#52616c] font-bold">{k}</dt><dd className="text-[13px] font-black text-right">{v}</dd></div>)}
                </dl>
              </div>
            )}
            {(pr.services || []).length > 0 && (
              <div className="border border-[#cbd3d5] bg-white rounded-xl p-5" data-testid="pj-scope">
                <div className="text-[10px] font-black uppercase tracking-widest text-[#52616c]">Scope</div>
                <ul className="mt-2.5 space-y-1.5">
                  {pr.services.map((s) => <li key={s} className="text-[13px] font-semibold flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#a63c0b] shrink-0" /> {s}</li>)}
                </ul>
              </div>
            )}
          </aside>
        </div>
        {(pr.photos || []).length > 0 && (
          <div className="mt-10">
            <h2 className="font-display font-black uppercase text-xl tracking-tight">Project gallery</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5 mt-4">
              {pr.photos.map((path, i) => (
                <button key={i} onClick={() => setLightbox(path)} className="rounded-lg overflow-hidden group">
                  <img src={img(path)} alt={`${pr.name} photo ${i + 1}`} className="h-48 md:h-56 w-full object-cover group-hover:scale-[1.03] transition-transform duration-300" loading="lazy" />
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="mt-12 bg-[#e9eeec] text-[#142635] rounded-2xl p-8 md:p-10 text-center">
          <h3 className="font-display font-black uppercase text-2xl md:text-3xl">Planning something similar?</h3>
          <p className="text-[#52616c] mt-1.5 text-sm">Send your plans directly to {d.company.name}.</p>
          <button onClick={() => { setEnquire(true); track(slug, "quote_click"); }} className="mt-5 bg-[#FF5F15] hover:bg-[#E65100] text-[#142635] text-[12px] font-black uppercase tracking-wider px-8 py-3 rounded-md" data-testid="pj-quote">Request a quote</button>
        </div>
      </div>
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <img src={img(lightbox)} alt="" className="max-h-[90vh] max-w-full rounded-lg" />
        </div>
      )}
      {enquire && <EnquiryForm slug={slug} services={pr.services || []} onClose={() => setEnquire(false)} />}
      <MarketingFooter />
    </div>
  );
}
